import type { Attachment } from '@medplum/fhirtypes'
import {
  DOCUMENT_CATEGORY_OPTIONS,
  type DocumentCategory,
} from '@seen/core/models/document-references/constants'
import { cn } from '@seen/ui'
import { IconCategory, IconPencil } from '@tabler/icons-react'
import type { ReactNode } from 'react'
import { useMemo, useState } from 'react'
import { ResizableHandle } from '#src/components/core/resizable/ResizableHandle.tsx'
import { ResizablePanel } from '#src/components/core/resizable/ResizablePanel.tsx'
import { ResizablePanelGroup } from '#src/components/core/resizable/ResizablePanelGroup.tsx'
import { DocumentPickerDetail } from '#src/components/document-picker/DocumentPickerDetail.tsx'
import { DocumentPickerHeader } from '#src/components/document-picker/DocumentPickerHeader.tsx'
import { DocumentPickerList } from '#src/components/document-picker/DocumentPickerList.tsx'
import { DocumentPickerPreview } from '#src/components/document-picker/DocumentPickerPreview.tsx'
import type { Filter, FilterConfigMap } from '#src/components/filter/Filter.tsx'
import { FilterValue } from '#src/components/filter/useFilteredData.ts'

export interface DocumentPickerDocument {
  id: string
  title: string
  date: string
  categories?: DocumentCategory[]
  attachments?: Attachment[]
  isSigned?: boolean
  subtitle?: string | null
  /**
   * Raw description text (HTML stripped by caller, or plain text). Used for fuzzy
   * search indexing and match-snippet rendering.
   */
  description?: string | null
  /** AI-generated summary text. Boosted above description in search ranking. */
  aiSummary?: string | null
  /** Author display name for search indexing. */
  author?: string | null
  /** Transcript text from associated audio/video recordings. */
  transcription?: string | null
}

interface DocumentPickerViewProps {
  documents: DocumentPickerDocument[]
  selectedIds?: string[]
  onSelectionChange?: (selectedIds: string[]) => void
  activeDocumentId?: string | null
  onActiveDocumentChange?: (documentId: string) => void
  onItemDoubleClick?: (id: string) => void
  onLoadMore?: () => void
  hasMore?: boolean
  isLoadingMore?: boolean
  className?: string
  listFooter?: ReactNode
  onClose?: () => void
}

export function DocumentPickerView({
  documents,
  selectedIds,
  onSelectionChange,
  activeDocumentId,
  onActiveDocumentChange,
  onItemDoubleClick,
  onLoadMore,
  hasMore,
  isLoadingMore,
  className,
  listFooter,
  onClose,
}: DocumentPickerViewProps) {
  const selectedSet = useMemo(() => new Set(selectedIds ?? []), [selectedIds])
  const activeDocument = useMemo(
    () => documents.find((d) => d.id === activeDocumentId) ?? null,
    [documents, activeDocumentId]
  )

  const formattedDate = activeDocument?.date
    ? new Date(activeDocument.date).toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: '2-digit',
      })
    : undefined

  const [filters, setFilters] = useState<Filter[]>([])

  const filterOptions = useMemo<FilterConfigMap<DocumentPickerDocument>>(
    () => ({
      signatureStatus: {
        label: 'Signature',
        icon: <IconPencil className="size-4" />,
        type: 'multi-select',
        options: [
          { label: 'Signed', value: 'signed', keywords: ['signed'] },
          {
            label: 'No signature',
            value: 'no-signature',
            keywords: ['no', 'unsigned'],
          },
        ],
        valueFn: (doc) => (doc.isSigned ? 'signed' : 'no-signature'),
      },
      category: {
        label: 'Category',
        icon: <IconCategory className="size-4" />,
        type: 'multi-select-array',
        options: [
          {
            value: FilterValue.None,
            label: 'No category',
            keywords: ['no category', 'uncategorized'],
          },
          ...DOCUMENT_CATEGORY_OPTIONS.map((option) => ({
            ...option,
            keywords: [option.label],
          })),
        ],
        valueFn: (doc) => doc.categories ?? [],
      },
    }),
    []
  )

  return (
    <ResizablePanelGroup
      direction="horizontal"
      className={cn('h-full', className)}
    >
      <ResizablePanel id="document-picker-list" defaultSize={22} minSize={15}>
        <div className="relative h-full">
          <DocumentPickerList
            documents={documents}
            selectedIds={selectedSet}
            activeDocumentId={activeDocumentId}
            onSelectionChange={onSelectionChange}
            onActiveDocumentChange={onActiveDocumentChange}
            onItemDoubleClick={onItemDoubleClick}
            onLoadMore={onLoadMore}
            hasMore={hasMore}
            isLoadingMore={isLoadingMore}
            filters={filters}
            filterOptions={filterOptions}
          />
          {listFooter}
        </div>
      </ResizablePanel>
      <ResizableHandle />
      <ResizablePanel id="document-picker-main" defaultSize={78} minSize={40}>
        <div className="flex h-full flex-col">
          <DocumentPickerHeader
            filterOptions={filterOptions}
            onFiltersChange={setFilters}
            documentId={activeDocumentId}
            onClose={onClose}
          />
          <ResizablePanelGroup
            direction="horizontal"
            className="min-h-0 flex-1"
          >
            <ResizablePanel
              id="document-picker-preview"
              defaultSize={72}
              minSize={30}
            >
              <div className="flex h-full flex-col">
                {activeDocument && (
                  <div className="flex min-w-0 shrink-0 items-center border-b px-4 py-1.5 text-sm">
                    <span className="truncate font-medium">
                      {activeDocument.title}
                    </span>
                    {formattedDate && (
                      <span className="shrink-0 text-muted-foreground">
                        &nbsp;&middot; {formattedDate}
                      </span>
                    )}
                  </div>
                )}
                <DocumentPickerPreview
                  document={activeDocument}
                  className="min-h-0 flex-1"
                />
              </div>
            </ResizablePanel>
            <ResizableHandle />
            <ResizablePanel id="document-picker-detail" defaultSize={28}>
              <DocumentPickerDetail documentId={activeDocumentId} />
            </ResizablePanel>
          </ResizablePanelGroup>
        </div>
      </ResizablePanel>
    </ResizablePanelGroup>
  )
}
